/**
 * Engagement Repository
 *
 * Extends BaseRepository with engagement-specific query methods.
 * Read-only: the engagement collection is written to by the recordEngagement
 * Cloud Function on the homepage; the dashboard only reads.
 */

import { query, where, orderBy as fsOrderBy, limit as fsLimit, getDocs, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Engagement } from './Engagement';
import { BaseRepository } from './BaseRepository';
import { withTimeout } from '../utils/withTimeout';

// 'All time' (no date bounds) would otherwise stream the entire engagement
// collection. Cap it to the most-recent N so a huge unbounded read can't hang
// the UI (and so funnel stats stay over a representative recent sample).
const ALL_TIME_CAP = 5000;

export class EngagementRepository extends BaseRepository {
  constructor(db, functions) {
    super(db, functions, 'engagement', Engagement);
  }

  // ========================================
  // SPECIALIZED FINDER METHODS
  // ========================================

  /**
   * Find sessions within a date range, newest first.
   *
   * Requires composite index: (environment ASC, lastSeen ASC)
   * The index is defined in firestore.indexes.json.
   *
   * @param {Date|null} startDate
   * @param {Date|null} endDate
   * @param {string}    environment  'production' | 'staging' | 'all'
   * @param {Object}    [opts]
   * @param {boolean}   [opts.convertedOnly]  Only return sessions with dealId
   * @param {number}    [opts.limit]
   * @returns {Promise<Engagement[]>}
   */
  async findByDateRange(startDate = null, endDate = null, environment = 'production', opts = {}) {
    const constraints = [];

    if (environment !== 'all') {
      constraints.push(where('environment', '==', environment));
    }

    if (startDate) {
      const ts = startDate instanceof Date ? Timestamp.fromDate(startDate) : startDate;
      constraints.push(where('lastSeen', '>=', ts));
    }

    if (endDate) {
      const ts = endDate instanceof Date ? Timestamp.fromDate(endDate) : endDate;
      constraints.push(where('lastSeen', '<=', ts));
    }

    constraints.push(fsOrderBy('lastSeen', 'desc'));

    // Cap the result set. An explicit caller limit wins; otherwise 'all time'
    // (no date bounds) is capped to ALL_TIME_CAP most-recent sessions.
    const effectiveLimit = opts.limit || (!startDate && !endDate ? ALL_TIME_CAP : null);
    if (effectiveLimit) {
      constraints.push(fsLimit(effectiveLimit));
    }

    const q = query(this.getCollection(), ...constraints);
    // Guard the read: if a wedged Safari channel never settles getDocs, withTimeout
    // rejects after 20s so useEngagements clears its spinner and shows Retry,
    // instead of spinning forever.
    const snapshot = await withTimeout(
      getDocs(q),
      20000,
      'Engagement query timed out — the connection may be unstable. Please retry.',
    );
    let results = snapshot.docs.map(doc => Engagement.fromFirestore(doc));

    if (opts.convertedOnly) {
      results = results.filter(s => s.isConverted);
    }

    return results;
  }

  /**
   * Find all converted sessions (those with a dealId), newest first.
   *
   * Requires composite index: (environment ASC, dealId ASC)
   *
   * @param {string} environment
   * @returns {Promise<Engagement[]>}
   */
  async findConverted(environment = 'production') {
    const constraints = [
      where('environment', '==', environment),
      where('dealId', '!=', null),
      fsOrderBy('dealId'),   // required by Firestore when using !=
      fsOrderBy('lastSeen', 'desc'),
    ];

    const q = query(this.getCollection(), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => Engagement.fromFirestore(doc));
  }

  /**
   * Find the most recently active sessions (for the Live tab / dashboard card).
   *
   * @param {string} environment
   * @param {number} maxCount
   * @returns {Promise<Engagement[]>}
   */
  async findRecent(environment = 'production', maxCount = 20) {
    return this.findByDateRange(null, null, environment, { limit: maxCount });
  }

  /**
   * Funnel summary computed server-side (getEngagementFunnelSummaryCallable).
   * Returns the small aggregate object instead of downloading every session, so
   * the Funnel tab never pulls thousands of event-bearing docs.
   *
   * @param {Object} opts { environment, startDate, endDate }
   * @returns {Promise<Object>} { total, sliders, ctaClicks, quotes, sectionCounts,
   *   priceBucketCounts, ctaBySource, ctaClicksBySource, trafficBySource,
   *   referrersBySource, videoPlaysBySource, videoPlays, medianPrice, scanned, capped }
   */
  async getFunnelSummary({ environment = 'production', startDate = null, endDate = null, hideInternal = true, hideBots = false, filter = null } = {}) {
    const fn = httpsCallable(this.functions, 'getEngagementFunnelSummaryCallable');
    const toMs = (d) => (d == null ? null : d instanceof Date ? d.getTime() : d);
    const res = await withTimeout(
      fn({ environment, startMs: toMs(startDate), endMs: toMs(endDate), hideInternal, hideBots, filter }),
      30000,
      'Funnel summary timed out — the connection may be unstable. Please retry.',
    );
    return res.data;
  }

  /**
   * Per-page module activity for the Pages-lens drill-down
   * (getEngagementPageDetailCallable). Respects the same segment filter.
   */
  async getPageDetail({ environment = 'production', startDate = null, endDate = null, page = '/', filter = null } = {}) {
    const fn = httpsCallable(this.functions, 'getEngagementPageDetailCallable');
    const toMs = (d) => (d == null ? null : d instanceof Date ? d.getTime() : d);
    const res = await withTimeout(
      fn({ environment, startMs: toMs(startDate), endMs: toMs(endDate), page, filter }),
      30000,
      'Page detail timed out — the connection may be unstable. Please retry.',
    );
    return res.data;
  }

  /**
   * Conversions list computed server-side (getEngagementConversionsCallable):
   * the converted (quote-submitting) sessions in range, enriched with their
   * HubSpot deal (property name, postcode, stage, value, created/closed dates).
   * The cookie-free plane — origination + activity from the quote visit onward.
   *
   * @param {Object} opts { environment, startDate, endDate }
   * @returns {Promise<{conversions: Object[], scanned: number, capped: boolean}>}
   */
  async getConversions({ environment = 'production', startDate = null, endDate = null, hideInternal = true } = {}) {
    const fn = httpsCallable(this.functions, 'getEngagementConversionsCallable');
    const toMs = (d) => (d == null ? null : d instanceof Date ? d.getTime() : d);
    const res = await withTimeout(
      fn({ environment, startMs: toMs(startDate), endMs: toMs(endDate), hideInternal }),
      30000,
      'Conversions timed out — the connection may be unstable. Please retry.',
    );
    return res.data;
  }

  /**
   * Visitors list computed server-side (getEngagementVisitorsCallable): the
   * Sessions list grouped by visitor (hubspotutk, falling back to quoteId for
   * cookie-free resumed quotes) instead of by raw session.
   *
   * @param {Object} opts { environment, startDate, endDate }
   * @returns {Promise<{visitors: Object[], scanned: number, capped: boolean}>}
   */
  async getVisitors({ environment = 'production', startDate = null, endDate = null, hideInternal = true } = {}) {
    const fn = httpsCallable(this.functions, 'getEngagementVisitorsCallable');
    const toMs = (d) => (d == null ? null : d instanceof Date ? d.getTime() : d);
    const res = await withTimeout(
      fn({ environment, startMs: toMs(startDate), endMs: toMs(endDate), hideInternal }),
      30000,
      'Visitors timed out — the connection may be unstable. Please retry.',
    );
    return res.data;
  }

  /**
   * Find all sessions belonging to the same visitor: those sharing a
   * `hubspotutk` (consented cross-session link) and/or a `quoteId` (quote-journey
   * link). Both are single-field equality queries (Firestore auto-indexes them),
   * unioned + de-duplicated. Bounded to 50 each.
   *
   * @param {Object} opts { hubspotutk, quoteId }
   * @returns {Promise<Engagement[]>}
   */
  async findLinkedSessions({ hubspotutk = null, quoteId = null } = {}) {
    const queries = [];
    if (hubspotutk) queries.push(query(this.getCollection(), where('hubspotutk', '==', hubspotutk), fsLimit(50)));
    if (quoteId) queries.push(query(this.getCollection(), where('quoteId', '==', quoteId), fsLimit(50)));
    if (!queries.length) return [];

    const snaps = await Promise.all(queries.map((q) =>
      withTimeout(getDocs(q), 15000, 'Linked sessions query timed out — please retry.')));

    const map = new Map();
    snaps.forEach((snap) => snap.docs.forEach((d) => {
      if (!map.has(d.id)) map.set(d.id, Engagement.fromFirestore(d));
    }));
    return [...map.values()];
  }

  /**
   * Manually mark/unmark a visitor as internal traffic (setSessionGroupInternalCallable)
   * — for historical sessions IP-based classification can't reach, or a visit
   * the team forgot to flag live via the InternalIpBanner. Resolves the FULL
   * linked group server-side (hubspotutk/quoteId), so marking one session marks
   * every linked session consistently.
   *
   * @param {Object} opts { sessionId, hubspotutk, quoteId, isInternal }
   * @returns {Promise<{sessionIds: string[], isInternal: boolean}>}
   */
  async setSessionGroupInternal({ sessionId, hubspotutk = null, quoteId = null, isInternal = true } = {}) {
    const fn = httpsCallable(this.functions, 'setSessionGroupInternalCallable');
    const res = await withTimeout(
      fn({ sessionId, hubspotutk, quoteId, isInternal }),
      15000,
      'Updating internal-traffic flag timed out — please retry.',
    );
    return res.data;
  }
}
